import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Globe, 
  MapPin, 
  Calendar,
  RefreshCw,
  DollarSign,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface EconomicIndicator {
  id: string;
  name: string;
  value: number;
  change: number;
  changeType: 'positive' | 'negative';
  unit: string;
  lastUpdated: string;
}

interface ChartData {
  date: string;
  value: number;
}

const Dashboard: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<'india' | 'world'>('india');
  const [indicators, setIndicators] = useState<EconomicIndicator[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Mock data - In real app, this would come from API
  const mockIndiaData: EconomicIndicator[] = [
    {
      id: 'gdp',
      name: 'GDP Growth Rate',
      value: 6.3,
      change: 0.2,
      changeType: 'positive',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'inflation',
      name: 'Inflation Rate (CPI)',
      value: 5.69,
      change: -0.31,
      changeType: 'positive',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'unemployment',
      name: 'Unemployment Rate',
      value: 3.2,
      change: -0.1,
      changeType: 'positive',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'repo-rate',
      name: 'Repo Rate',
      value: 6.5,
      change: 0,
      changeType: 'positive',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'forex',
      name: 'USD/INR',
      value: 83.25,
      change: 0.15,
      changeType: 'negative',
      unit: '₹',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'sensex',
      name: 'BSE Sensex',
      value: 72000,
      change: 250.5,
      changeType: 'positive',
      unit: 'points',
      lastUpdated: '2024-01-15'
    }
  ];

  const mockWorldData: EconomicIndicator[] = [
    {
      id: 'global-gdp',
      name: 'Global GDP Growth',
      value: 3.1,
      change: -0.1,
      changeType: 'negative',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'us-inflation',
      name: 'US Inflation Rate',
      value: 3.4,
      change: -0.2,
      changeType: 'positive',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'fed-rate',
      name: 'Federal Funds Rate',
      value: 5.25,
      change: 0,
      changeType: 'positive',
      unit: '%',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'oil-price',
      name: 'Crude Oil (Brent)',
      value: 82.50,
      change: 1.25,
      changeType: 'positive',
      unit: '$/barrel',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'gold-price',
      name: 'Gold Price',
      value: 2025,
      change: -5.2,
      changeType: 'negative',
      unit: '$/oz',
      lastUpdated: '2024-01-15'
    },
    {
      id: 'dxy',
      name: 'Dollar Index (DXY)',
      value: 103.25,
      change: 0.35,
      changeType: 'positive',
      unit: 'index',
      lastUpdated: '2024-01-15'
    }
  ];

  const mockChartData: ChartData[] = [
    { date: '2024-01-01', value: 6.1 },
    { date: '2024-01-02', value: 6.2 },
    { date: '2024-01-03', value: 6.0 },
    { date: '2024-01-04', value: 6.3 },
    { date: '2024-01-05', value: 6.4 },
    { date: '2024-01-06', value: 6.2 },
    { date: '2024-01-07', value: 6.3 },
  ];

  useEffect(() => {
    // Simulate API call
    setLoading(true);
    setTimeout(() => {
      setIndicators(selectedRegion === 'india' ? mockIndiaData : mockWorldData);
      setChartData(mockChartData);
      setLoading(false);
    }, 1000);
  }, [selectedRegion, mockIndiaData, mockWorldData, mockChartData]);

  const refreshData = () => {
    setLastRefresh(new Date());
    setLoading(true);
    // Simulate API refresh
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Economics Dashboard</h1>
          <p className="mt-2 text-gray-600">Real-time economic indicators and market data</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setSelectedRegion('india')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedRegion === 'india'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <MapPin className="h-4 w-4 mr-2" />
              India
            </button>
            <button
              onClick={() => setSelectedRegion('world')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedRegion === 'world'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Globe className="h-4 w-4 mr-2" />
              World
            </button>
          </div>
          <button
            onClick={refreshData}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="flex items-center text-sm text-gray-500">
        <Calendar className="h-4 w-4 mr-2" />
        Last updated: {lastRefresh.toLocaleString()}
      </div>

      {/* Economic Indicators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="metric-card animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          ))
        ) : (
          indicators.map((indicator) => (
            <div key={indicator.id} className="metric-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">{indicator.name}</h3>
                <div className={`p-1 rounded-full ${
                  indicator.changeType === 'positive' ? 'bg-success-50' : 'bg-danger-50'
                }`}>
                  {indicator.changeType === 'positive' ? (
                    <TrendingUp className="h-4 w-4 text-success-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-danger-600" />
                  )}
                </div>
              </div>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-gray-900">
                  {indicator.value.toLocaleString()}
                </span>
                <span className="text-sm text-gray-500">{indicator.unit}</span>
              </div>
              <div className="flex items-center mt-2">
                <span className={`text-sm font-medium ${
                  indicator.changeType === 'positive' ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {indicator.change > 0 ? '+' : ''}{indicator.change}
                </span>
                <span className="text-xs text-gray-500 ml-1">from last period</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* GDP Growth Trend */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">GDP Growth Trend</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Market Performance */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Market Performance</h3>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8" />
            <div className="ml-4">
              <p className="text-blue-100">Market Cap</p>
              <p className="text-2xl font-bold">₹280T</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8" />
            <div className="ml-4">
              <p className="text-green-100">FII Inflow</p>
              <p className="text-2xl font-bold">₹15,420Cr</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <PieChart className="h-8 w-8" />
            <div className="ml-4">
              <p className="text-purple-100">Sectors</p>
              <p className="text-2xl font-bold">11 Green</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8" />
            <div className="ml-4">
              <p className="text-orange-100">Volume</p>
              <p className="text-2xl font-bold">₹85,420Cr</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 